import { useEffect, useState } from "react";
import { Loader2, LogIn } from "lucide-react";
import { staffApi, errMsg } from "./api";
import { AuthShell, Field, Status } from "./Shell";
import { RecoveryCodes } from "./RecoveryCodes";

const Submit = ({ loading, children, testId }) => (
  <button type="submit" disabled={loading} className="btn btn-navy w-full disabled:opacity-60" data-testid={testId}>
    {loading ? <Loader2 size={16} className="animate-spin" /> : <LogIn size={16} />} {children}
  </button>
);

const LinkBtn = ({ onClick, children, testId }) => (
  <button type="button" onClick={onClick} className="text-sm font-semibold text-navy underline underline-offset-4 hover:text-gold-deep" data-testid={testId}>
    {children}
  </button>
);

const Forgot = ({ onBack }) => {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState({});
  const submit = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading" });
    try {
      const { data } = await staffApi.post("/auth/forgot", { email });
      setStatus({ state: "success", msg: data?.message ?? "Check your email for a link." });
    } catch (err) {
      setStatus({ state: "error", msg: await errMsg(err) });
    }
  };
  return (
    <form onSubmit={submit} className="space-y-6" data-testid="staff-forgot-form">
      <p className="text-sm text-slate">Enter your staff email. We'll send a one-time link to set up or reset your password. The link expires in 1 hour (48 hours for new accounts).</p>
      <Field label="Staff email" type="email" required autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="staff-forgot-email-input" />
      <Status status={status} testId="staff-forgot-status" />
      <Submit loading={status.state === "loading"} testId="staff-forgot-submit-button">Send Link</Submit>
      <LinkBtn onClick={onBack} testId="staff-forgot-back-button">Back to sign in</LinkBtn>
    </form>
  );
};

const CodeForm = ({ challenge, setup, onSuccess }) => {
  const [code, setCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const [status, setStatus] = useState({});
  const submit = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading" });
    try {
      const { data } = await staffApi.post("/auth/verify", { challenge, code });
      onSuccess(data);
    } catch (err) {
      setStatus({ state: "error", msg: await errMsg(err) });
    }
  };
  return (
    <form onSubmit={submit} className="space-y-6" data-testid="staff-code-form">
      <Field label={useRecovery ? "Recovery code" : "6-digit code from your authenticator app"} required
        inputMode={useRecovery ? "text" : "numeric"} autoComplete="one-time-code" maxLength={useRecovery ? 16 : 6}
        value={code} onChange={(e) => setCode(e.target.value)} data-testid="staff-code-input" />
      <Status status={status} testId="staff-code-status" />
      <Submit loading={status.state === "loading"} testId="staff-code-submit-button">{setup ? "Turn On Two-Step Sign-In" : "Verify"}</Submit>
      {!setup && (
        <LinkBtn onClick={() => { setUseRecovery(!useRecovery); setCode(""); }} testId="staff-code-toggle-recovery-button">
          {useRecovery ? "Use my authenticator app instead" : "Lost your phone? Use a recovery code"}
        </LinkBtn>
      )}
    </form>
  );
};

const SetupGuide = ({ qr, secret }) => (
  <div className="mb-8" data-testid="staff-2fa-setup">
    <p className="text-sm font-semibold text-navy">Set up two-step sign-in (required)</p>
    <ol className="mt-3 list-decimal space-y-1 pl-5 text-sm text-slate">
      <li>Open Google Authenticator, Microsoft Authenticator, or another authenticator app.</li>
      <li>Add an account and scan this QR code.</li>
      <li>Enter the 6-digit code the app shows.</li>
    </ol>
    <p className="mt-3 text-xs text-slate">Tried before? Delete any older "First Responders Foundation" entries in your app first, then scan this code. This QR code stays the same until setup is finished.</p>
    <img src={qr} alt="QR code for your authenticator app" className="mt-5 h-44 w-44 border border-mist" data-testid="staff-2fa-qr" />
    <p className="mt-4 text-xs text-slate">Can't scan? Enter this key in the app instead:</p>
    <p className="mt-1 break-all font-mono text-sm text-navy" data-testid="staff-2fa-secret">{secret}</p>
  </div>
);

export const SignIn = ({ onSignedIn }) => {
  const [step, setStep] = useState("login");
  const [form, setForm] = useState({ email: "", password: "" });
  const [ctx, setCtx] = useState(null);
  const [status, setStatus] = useState({});
  useEffect(() => { window.scrollTo(0, 0); }, [step]);

  const login = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading" });
    try {
      const { data } = await staffApi.post("/auth/login", form);
      setCtx(data);
      setStep(data?.step === "setup" ? "setup" : "verify");
      setStatus({});
    } catch (err) {
      setStatus({ state: "error", msg: await errMsg(err) });
    }
  };

  const verified = (data) => {
    if (Array.isArray(data?.recovery_codes)) {
      setCtx({ ...ctx, codes: data.recovery_codes, user: data.user });
      setStep("codes");
    } else onSignedIn(data?.user, data?.used_recovery_code);
  };

  const titles = { login: "Staff Sign In", forgot: "Set Up or Reset Password", setup: "Two-Step Sign-In", verify: "Two-Step Sign-In", codes: "Recovery Codes" };
  return (
    <AuthShell testId="staff-signin-page" title={titles[step]}
      intro="For authorized Foundation staff only. Please handle all records confidentially.">
      {step === "login" && (
        <form onSubmit={login} className="space-y-6" data-testid="staff-login-form">
          <Field label="Staff email" type="email" required autoComplete="username" value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="staff-login-email-input" />
          <Field label="Password" type="password" required autoComplete="current-password" value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="staff-login-password-input" />
          <Status status={status} testId="staff-login-status" />
          <Submit loading={status.state === "loading"} testId="staff-login-submit-button">Continue</Submit>
          <LinkBtn onClick={() => setStep("forgot")} testId="staff-login-forgot-button">First time, or forgot your password?</LinkBtn>
        </form>
      )}
      {step === "forgot" && <Forgot onBack={() => setStep("login")} />}
      {step === "setup" && <SetupGuide qr={ctx?.qr} secret={ctx?.secret} />}
      {(step === "setup" || step === "verify") && <CodeForm challenge={ctx?.challenge} setup={step === "setup"} onSuccess={verified} />}
      {step === "codes" && <RecoveryCodes codes={ctx?.codes ?? []} onDone={() => onSignedIn(ctx?.user)} doneLabel="I've saved my codes, continue" />}
    </AuthShell>
  );
};
